/* Premium Signal Card — Terminal-EX reference design.
   Tek satırlı, 4 kolonlu, yön rengiyle (UP yeşil / DOWN kırmızı) borderlı. */
import { Activity } from "lucide-react";
import CoinIcon from "./CoinIcon";

const confidenceColor = (c) => {
  if (c >= 65) return "#4ade80"; // emerald
  if (c >= 45) return "#f0b90b"; // amber
  if (c >= 35) return "#ff9a5e"; // orange
  return "#ff5662";              // rose
};

const fmt = (n, d = 4) => (n == null ? "—" : Number(n).toFixed(d));

const UpIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M3 17l6-6 4 4 8-8M15 7h6v6"
          strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const DownIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M3 7l6 6 4-4 8 8M15 17h6v-6"
          strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const SignalCard = ({ signal, onOpen, opening = false }) => {
  const conf = signal.confidence ?? signal.score ?? 0;
  const isUp = signal.direction === "UP";
  const dirClass = isUp ? "up" : "down";
  const rrLabel = signal.risk_reward
    ? `R/R 1:${Number(signal.risk_reward).toFixed(2)}`
    : "R/R —";
  const movePct = signal.expected_move_pct != null
    ? `${signal.expected_move_pct >= 0 ? "+" : ""}${Number(signal.expected_move_pct).toFixed(2)}%`
    : "";

  return (
    <div
      className={`tx-sig ${dirClass}`}
      data-testid={`signal-card-${signal.symbol}`}
    >
      <div className="tx-sig-top">
        <div className="tx-sig-id">
          <div className="tx-sig-ic" aria-hidden>
            {isUp ? <UpIcon /> : <DownIcon />}
          </div>
          <CoinIcon symbol={signal.symbol} size="lg" />
          <div>
            <div className="sym">{signal.symbol}</div>
            <div className="dir">
              {signal.direction || "—"} ·{" "}
              <span className="tg">
                {signal.counter_side}/{signal.darkex_side}
              </span>
            </div>
          </div>
        </div>

        <div className="tx-sig-cols">
          <div className="col">
            <span className="cl">Giriş</span>
            <span className="cv" data-testid={`signal-entry-${signal.symbol}`}>
              {fmt(signal.entry, 4)}
            </span>
          </div>
          <div className="col">
            <span className="cl red">Stop</span>
            <span className="cv red" data-testid={`signal-sl-${signal.symbol}`}>
              {fmt(signal.stop_loss, 4)}
            </span>
          </div>
          <div className="col">
            <span className="cl grn">Hedef</span>
            <span className="cv grn" data-testid={`signal-tp-${signal.symbol}`}>
              {fmt(signal.take_profit, 4)}
            </span>
          </div>
          <div className="col conf">
            <span className="cl">Güven</span>
            <span className="score" style={{ color: confidenceColor(conf) }}>
              {Math.round(conf)}
              <small>/100</small>
            </span>
            <div className="bar">
              <i style={{
                background: confidenceColor(conf),
                width: `${Math.min(100, conf)}%`,
              }} />
            </div>
            <span className="rr">
              {rrLabel}
              {movePct && <> · <b>{movePct}</b></>}
            </span>
          </div>
        </div>

        <button
          type="button"
          className="tx-sig-btn"
          onClick={() => onOpen?.(signal)}
          disabled={opening}
          data-testid={`signal-open-${signal.symbol}`}
        >
          {opening ? "Açılıyor…" : "Pozisyon Aç"}
        </button>
      </div>

      {signal.reasons?.length > 0 && (
        <div className="tx-sig-det">
          <div className="txt">
            {signal.reasons.slice(0, 6).map((r, i) => (
              <span key={i}>
                {i > 0 && " · "}
                {r}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const SignalCardSkeleton = () => (
  <div className="tx-sig up" style={{ opacity: 0.4 }}>
    <div className="tx-sig-top">
      <div className="tx-sig-id">
        <div className="tx-sig-ic" />
        <div>
          <div className="sym" style={{
            background: "rgba(255,255,255,0.05)",
            width: 70, height: 14, borderRadius: 4,
          }} />
          <div className="dir" style={{
            background: "rgba(255,255,255,0.04)",
            width: 100, height: 10, borderRadius: 4, marginTop: 4,
          }} />
        </div>
      </div>
      <div className="tx-sig-cols">
        {[0, 1, 2, 3].map((i) => (
          <div className="col" key={i}>
            <span className="cl" style={{
              background: "rgba(255,255,255,0.04)",
              height: 10, width: 30, borderRadius: 3,
            }} />
            <span className="cv" style={{
              background: "rgba(255,255,255,0.05)",
              height: 13, width: 60, marginTop: 4, borderRadius: 3,
            }} />
          </div>
        ))}
      </div>
      <button className="tx-sig-btn" disabled style={{ opacity: 0.4 }}>
        ⋯
      </button>
    </div>
  </div>
);

export const EmptySignals = ({ onScan, scanning }) => (
  <div
    className="tx-card"
    style={{ padding: "32px 24px", textAlign: "center" }}
    data-testid="empty-signals"
  >
    <span className="tx-cb3" />
    <span className="tx-cb4" />
    <div style={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 56,
      height: 56,
      background: "rgba(255,255,255,0.03)",
      border: "1px solid rgba(255,255,255,0.06)",
      borderRadius: 12,
      marginBottom: 16,
    }}>
      <Activity style={{ color: "#6b7280", width: 26, height: 26 }} />
    </div>
    <div style={{ fontSize: 14, fontWeight: 600, color: "#e6e9f0",
                  marginBottom: 4 }}>
      Henüz aktif sinyal yok
    </div>
    <div style={{ fontSize: 12, color: "#6b7280", maxWidth: 460,
                  margin: "0 auto 18px", lineHeight: 1.55 }}>
      Motor sürekli derinlik, akış ve hacmi tarıyor. Belirlediğin eşiğin
      üstünde bir fırsat görüldüğünde anında burada listelenecek.
    </div>
    {onScan && (
      <button
        type="button"
        className="tx-sig-btn"
        onClick={onScan}
        disabled={scanning}
        data-testid="signals-scan-now"
      >
        {scanning ? "Taranıyor…" : "⚡ Şimdi Tara"}
      </button>
    )}
  </div>
);
